import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ReviewForm } from '../components/ReviewForm';
import { useCreateReview, useReview, useUpdateReview } from '../queries';
import type { PerformanceReviewInput } from '../types';

export function ReviewFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const navigate = useNavigate();

  const reviewQuery = useReview(isEdit ? publicId : -1);
  const create = useCreateReview();
  const update = useUpdateReview(publicId);

  async function handleSubmit(input: PerformanceReviewInput) {
    const saved = isEdit ? await update.mutateAsync(input) : await create.mutateAsync(input);
    navigate(`/hrm/reviews/${saved.public_id}`);
  }

  if (isEdit && reviewQuery.isPending) {
    return (
      <div className="w-full max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const review = reviewQuery.data;

  return (
    <div className="w-full max-w-3xl space-y-8">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/hrm/reviews/${publicId}` : '/hrm/reviews')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit review' : 'New review'}
        </h1>
      </div>

      <ReviewForm
        submitLabel={isEdit ? 'Save changes' : 'Create review'}
        showActionItems={!isEdit}
        defaultValues={
          review
            ? {
                employee: review.employee ? String(review.employee) : '',
                reviewer: review.reviewer ? String(review.reviewer) : '',
                period: review.period ?? '',
                rating: review.rating != null ? String(review.rating) : '',
                summary: review.summary ?? '',
                status: review.status,
                review_date: review.review_date ?? '',
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/hrm/reviews/${publicId}` : '/hrm/reviews')}
      />
    </div>
  );
}
